import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { enrollmentAPI, paymentAPI, courseAPI } from '../lib/studentAPI';
import type { CourseEnrollment, Payment, Course } from '../types';
import {
  LogOut, User, BookOpen, CreditCard, Trophy, Loader2,
  CheckCircle, XCircle, Home, Clock, Lock, Unlock, Calendar,
} from 'lucide-react';

interface EnrichedEnrollment {
  id: string;
  studentId: string;
  courseId: string;
  progress: number;
  completedAt?: Date | null;
  enrolledAt: Date;
  status: string;

  course?: Course | null;

  accessStatus: {
    canAccess: boolean;
    reason: string;
    message: string;
    label: string;
    variant: 'default' | 'destructive' | 'secondary' | 'outline';
    action: string;
  };

  isLockedByStartDate?: boolean;
  lockReason?: string;
  startDate?: Date | null;

  nextBillingDate?: Date | null;
  waitingForStartDate?: boolean;
  isUnlocked?: boolean;
}

export const DashboardPage = () => {
  const { student, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [enrollments, setEnrollments] = useState<EnrichedEnrollment[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [enrollingCourseId, setEnrollingCourseId] = useState<string | null>(null);
  const [selectedCourseForEnrollment, setSelectedCourseForEnrollment] = useState<Course | null>(null);
  const [enrolling, setEnrolling] = useState(false);

  const [modal, setModal] = useState<{
    show: boolean;
    type: "success" | "error";
    message: string;
  }>({ show: false, type: "success", message: "" });

  const showModal = (type: "success" | "error", message: string) => {
    setModal({ show: true, type, message });
  };

  // Auto-hide modal
  useEffect(() => {
    if (modal.show) {
      const timer = setTimeout(() => setModal(prev => ({ ...prev, show: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [modal.show]);

  // Redirect if not logged in
  useEffect(() => {
    if (!student) {
      navigate('/login', { replace: true });
    }
  }, [student, navigate]);

  // Handle deep link from landing page
  useEffect(() => {
    const state = location.state as any;
    if (state?.enrollCourseId) {
      const course = allCourses.find(c => c.id === state.enrollCourseId);
      setSelectedCourseForEnrollment(course || null);
      setEnrollingCourseId(state.enrollCourseId);
      setEnrollDialogOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname, allCourses]);

  const normalizeArray = (res: any): any[] => {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.enrollments)) return res.enrollments;
    if (Array.isArray(res?.payments)) return res.payments;
    if (Array.isArray(res?.courses)) return res.courses;
    return [];
  };

  // Get enrollments for current student
  const getMyEnrollments = async () => {
    if (!student) return [];
    return await enrollmentAPI.getMyEnrollments();
  };

  // Memoized data loader
  const loadDashboardData = useCallback(async () => {
    if (!student) return;

    try {
      setLoading(true);

      const [enrollmentsData, paymentsRes, coursesRes] = await Promise.all([
        getMyEnrollments(),
        paymentAPI.getMyPayments(),
        courseAPI.getPublishedCourses(),
      ]);

      const coursesData = normalizeArray(coursesRes);
      
      // Build courses map
      const coursesMap = new Map(coursesData.map((c: Course) => [c.id, c]));

      // Enrich enrollments with course data and lock information
      const enrichedEnrollments: EnrichedEnrollment[] = enrollmentsData.map((enrollment: any) => {
        const course = coursesMap.get(enrollment.courseId);
        const now = new Date();
        const courseStartDate = course?.startDate ? new Date(course.startDate) : null;
        const hasReachedStartDate = courseStartDate ? now >= courseStartDate : true;
        
        const isLockedByStartDate = !hasReachedStartDate;
        const isUnlocked = enrollment.isUnlocked;
        const waitingForStartDate = enrollment.waitingForStartDate;
        
        let accessStatus: EnrichedEnrollment['accessStatus'] = {
          canAccess: false,
          reason: 'LOCKED',
          message: '',
          label: '',
          variant: 'destructive',
          action: '',
        };
        
        if (isLockedByStartDate) {
          accessStatus = {
            canAccess: false,
            reason: 'NOT_STARTED',
            message: `Course starts on ${courseStartDate?.toLocaleDateString()}`,
            label: 'Not Started',
            variant: 'secondary',
            action: `Starts ${courseStartDate?.toLocaleDateString()}`,
          };
        }
        else if (waitingForStartDate && !isLockedByStartDate) {
          accessStatus = {
            canAccess: false,
            reason: 'PAID_WAITING',
            message: `Starts ${courseStartDate?.toLocaleDateString()}`,
            label: 'Paid - Upcoming',
            variant: 'secondary',
            action: `Starts ${courseStartDate?.toLocaleDateString()}`,
          };
        }
        else if (isUnlocked) {
          const nextBillingDate = enrollment.nextBillingDate ? new Date(enrollment.nextBillingDate) : null;
          const isExpired = nextBillingDate && now > nextBillingDate;
          
          if (isExpired) {
            accessStatus = {
              canAccess: false,
              reason: 'EXPIRED',
              message: 'Subscription expired. Please renew.',
              label: 'Expired',
              variant: 'destructive',
              action: 'Renew',
            };
          } else {
            accessStatus = {
              canAccess: true,
              reason: 'ACTIVE',
              message: 'Access granted',
              label: 'Active',
              variant: 'default',
              action: 'Continue Learning',
            };
          }
        }
        else if (!isLockedByStartDate && !isUnlocked && !waitingForStartDate) {
          accessStatus = {
            canAccess: false,
            reason: 'NO_PAYMENT',
            message: 'Payment required to access this course',
            label: 'Locked',
            variant: 'destructive',
            action: 'Complete Payment',
          };
        }
        return {
          ...enrollment,
          course,
          accessStatus,
          isLockedByStartDate,
          lockReason: accessStatus.message,
          startDate: courseStartDate,
        };
      });

      setEnrollments(enrichedEnrollments);
      setPayments(normalizeArray(paymentsRes));
      setAllCourses(coursesData);

    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || "Failed to load dashboard data.";
      showModal("error", errorMsg);
      console.error("Dashboard load error:", error);
    } finally {
      setLoading(false);
    }
  }, [student]);

  useEffect(() => {
    if (student) {
      loadDashboardData();
    }
  }, [student, loadDashboardData]);

  useEffect(() => {
    if ((location.state as any)?.refreshEnrollments) {
      loadDashboardData();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, loadDashboardData, navigate, location.pathname]);

  const handleEnroll = async () => {
    if (!enrollingCourseId || !student) {
      showModal("error", "Missing course or student information");
      return;
    }

    setEnrolling(true);
    try {
      const response = await enrollmentAPI.enrollInCourse(enrollingCourseId);
      
      const message = (response as any)?.message || 
        (selectedCourseForEnrollment?.startDate && new Date(selectedCourseForEnrollment.startDate) > new Date()
          ? `Enrolled! Course will be available from ${new Date(selectedCourseForEnrollment.startDate).toLocaleDateString()}. Complete payment to unlock on start date.`
          : "Enrolled successfully! Complete payment to access course.");
      
      showModal("success", message);
      setEnrollDialogOpen(false);
      setEnrollingCourseId(null);
      setSelectedCourseForEnrollment(null);
      await loadDashboardData();
    } catch (error: any) {
      showModal("error", error.response?.data?.message || error.message || "Enrollment failed.");
    } finally {
      setEnrolling(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
    showModal("success", "Logged out successfully");
  };

  const handlePaymentRedirect = (enrollment: EnrichedEnrollment) => {
    navigate('/payments', { state: { selectedEnrollmentId: enrollment.id } });
  };

  const enrolledCourseIds = useMemo(
    () => new Set(enrollments.map((e) => e.courseId)),
    [enrollments]
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* HEADER - FIXED: full width on mobile */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* Logo section */}
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-white text-sm sm:text-base">LP</span>
              </div>
              <div>
                <h1 className="font-bold text-lg sm:text-xl">Learning Platform</h1>
                <p className="text-xs sm:text-sm text-gray-600">Student portal</p>
              </div>
            </div>

            {/* Navigation - wraps on mobile */}
            <div className="flex flex-wrap items-center gap-1 sm:gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-sm">
                <Home className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Home</span>
                <span className="inline sm:hidden text-xs">Home</span>
              </Button>

              <Button variant="ghost" size="sm" onClick={() => navigate('/payments')} className="text-sm">
                <CreditCard className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Payment</span>
                <span className="inline sm:hidden text-xs">Pay</span>
              </Button>

              <Button variant="ghost" size="sm" onClick={() => navigate('/challenges')} className="text-sm">
                <Trophy className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden md:inline">Monthly Innovative Challenge</span>
                <span className="inline md:hidden">Challenge</span>
                
              </Button>

              <Button variant="ghost" size="sm" onClick={() => navigate('/announcements')} className="text-sm">
                <BookOpen className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Announcements</span>
                <span className="inline sm:hidden text-xs">News</span>
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2">
                    <Avatar className="h-7 w-7 sm:h-8 sm:w-8">
                      <AvatarFallback className="bg-gradient-to-br from-blue-600 to-purple-600 text-white text-xs">
                        {student?.name?.charAt(0).toUpperCase() || 'S'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm">{student?.name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="h-4 w-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* MAIN CONTENT - FIXED: full width on mobile */}
      <main className="w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6 sm:mb-10">
          <h2 className="font-bold text-2xl sm:text-3xl mb-1 sm:mb-2">Welcome back, {student?.name?.split(' ')[0]}!</h2>
          <p className="text-sm sm:text-base text-gray-600">Continue your learning journey. Complete payment to unlock your courses.</p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 sm:h-10 sm:w-10 animate-spin text-blue-600" />
            <span className="ml-3 sm:ml-4 text-base sm:text-lg text-gray-600">Loading your dashboard...</span>
          </div>
        ) : (
          <div className="space-y-8 sm:space-y-12">
            {/* ==================== MY COURSES (ENROLLMENTS) ==================== */}
            <section>
              <h3 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                <BookOpen className="h-5 w-5 sm:h-6 sm:w-6" />
                My Courses
              </h3>

              {enrollments.length === 0 ? (
                <div className="text-center py-8 sm:py-12 bg-white rounded-2xl border">
                  <BookOpen className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base text-gray-500">You haven't enrolled in any courses yet.</p>
                  <Button 
                    variant="outline" 
                    className="mt-3 sm:mt-4"
                    onClick={() => document.getElementById('available-courses')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Browse Available Courses
                  </Button>
                </div>
              ) : (
                // FIXED: Responsive grid - single column on mobile, 2 on tablet, 3 on desktop
                <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                  {enrollments.map((enrollment) => {
                    const { accessStatus, isLockedByStartDate, startDate } = enrollment;
                    
                    return (
                      <div key={enrollment.id} className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border">
                        <div className="flex justify-between items-start gap-2 mb-3">
                          <h4 className="font-semibold text-base sm:text-lg line-clamp-2 flex-1">
                            {enrollment.course?.title || 'Course'}
                          </h4>
                          <Badge variant={accessStatus.variant} className="whitespace-nowrap text-xs sm:text-sm">
                            {accessStatus.label}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-2 text-xs sm:text-sm mb-3">
                          {accessStatus.canAccess ? (
                            <Unlock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <Lock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500 flex-shrink-0" />
                          )}
                          <span className="text-gray-600">{accessStatus.message}</span>
                        </div>

                        {isLockedByStartDate && startDate && (
                          <div className="flex items-center gap-2 text-xs sm:text-sm text-amber-600 bg-amber-50 p-2 rounded-lg mb-3">
                            <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                            <span>Starts: {new Date(startDate).toLocaleDateString()}</span>
                          </div>
                        )}

                        {enrollment.progress > 0 && (
                          <>
                            <Progress value={enrollment.progress || 0} className="mt-3" />
                            <p className="text-xs text-gray-500 mt-1">{enrollment.progress}% complete</p>
                          </>
                        )}

                        <div className="mt-4 sm:mt-5">
                          {accessStatus.canAccess ? (
                            <Button
                              className="w-full text-sm sm:text-base"
                              onClick={() => navigate(`/course/${enrollment.courseId}`)}
                            >
                              Continue Learning
                            </Button>
                          ) : enrollment.waitingForStartDate ? (
                            <Button className="w-full text-sm sm:text-base" variant="outline" disabled>
                              Starts {enrollment.startDate?.toLocaleDateString()}
                            </Button>
                          ) : !enrollment.isUnlocked ? (
                            <Button
                              className="w-full text-sm sm:text-base"
                              onClick={() => handlePaymentRedirect(enrollment)}
                            >
                              Complete Payment
                            </Button>
                          ) : (
                            <Button className="w-full text-sm sm:text-base" variant="outline" disabled>
                              {accessStatus.action || "Locked"}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* ==================== AVAILABLE COURSES ==================== */}
            <section id="available-courses">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 sm:mb-6">
                <h3 className="text-xl sm:text-2xl font-semibold">Available Courses</h3>
                <p className="text-xs sm:text-sm text-gray-500">Enroll and complete payment to start learning</p>
              </div>

              {allCourses.length === 0 ? (
                <p className="text-gray-500 text-center py-8 sm:py-12">No courses available at the moment.</p>
              ) : (
                // FIXED: Responsive grid - full width on mobile
                <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                  {allCourses.map((course, index) => {
                    const alreadyEnrolled = enrolledCourseIds.has(course.id);
                    const enrollment = enrollments.find((e) => e.courseId === course.id);
                    const courseStartDate = course.startDate ? new Date(course.startDate) : null;
                    const isFutureCourse = courseStartDate ? courseStartDate > new Date() : false;

                    return (
                      <motion.div
                        key={course.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.05 }}
                        className="group rounded-xl sm:rounded-2xl bg-white shadow-sm hover:shadow-lg transition-all overflow-hidden border border-gray-100"
                      >
                        {/* Course Image - responsive height */}
                        <div className="relative h-40 sm:h-48 bg-gradient-to-br from-blue-500 to-purple-600 overflow-hidden">
                          {course.coverImage ? (
                            <img
                              src={course.coverImage}
                              alt={course.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white text-5xl sm:text-7xl">📚</div>
                          )}
                          {isFutureCourse && (
                            <div className="absolute top-2 right-2 bg-blue-600/90 text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                              Starts {courseStartDate?.toLocaleDateString()}
                            </div>
                          )}
                        </div>

                        <div className="p-4 sm:p-6">
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-base sm:text-lg leading-tight mb-1 line-clamp-2">
                                {course.title}
                              </h4>
                              {course.subtitle && (
                                <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{course.subtitle}</p>
                              )}
                            </div>
                            {alreadyEnrolled && enrollment && (
                              <Badge variant={enrollment.accessStatus.variant} className="whitespace-nowrap text-xs">
                                {enrollment.accessStatus.label}
                              </Badge>
                            )}
                          </div>

                          {course.timeline && (
                            <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-600 mb-2">
                              <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                              <span>{course.timeline}</span>
                            </div>
                          )}
                          
                          {isFutureCourse && courseStartDate && (
                            <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-blue-600 mb-3 sm:mb-4">
                              <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                              <span>Course starts: {courseStartDate.toLocaleDateString()}</span>
                            </div>
                          )}

                          {course.objectives && course.objectives.length > 0 && (
                            <div className="mb-4 sm:mb-5">
                              <p className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-500 mb-1.5 sm:mb-2">What you'll learn</p>
                              <ul className="text-xs sm:text-sm text-gray-600 space-y-1">
                                {course.objectives.slice(0, 3).map((obj: string, i: number) => (
                                  <li key={i} className="flex items-start gap-1.5 sm:gap-2">
                                    <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                                    <span className="line-clamp-1 break-words">{obj}</span>
                                  </li>
                                ))}
                              </ul>
                              {course.objectives.length > 3 && (
                                <p className="text-[10px] sm:text-xs text-gray-500 mt-1">+{course.objectives.length - 3} more</p>
                              )}
                            </div>
                          )}

                          <div className="mt-4 sm:mt-6">
                            {alreadyEnrolled && enrollment ? (
                              <Button 
                                className="w-full text-sm sm:text-base" 
                                onClick={() => enrollment.accessStatus?.canAccess && navigate(`/course/${course.id}`)}
                                disabled={!enrollment.accessStatus?.canAccess}
                                variant={enrollment.accessStatus?.canAccess ? "default" : "outline"}
                              >
                                {enrollment.accessStatus?.action || (enrollment.accessStatus?.canAccess ? "Continue Learning" : "Locked")}
                              </Button>
                            ) : (
                              <Button
                                className="w-full bg-blue-600 hover:bg-blue-700 text-sm sm:text-base"
                                onClick={() => {
                                  setSelectedCourseForEnrollment(course);
                                  setEnrollingCourseId(course.id);
                                  setEnrollDialogOpen(true);
                                }}
                              >
                                Enroll Now
                              </Button>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}
      </main>

      {/* Enrollment Confirmation Dialog - FIXED: responsive width and positioning */}
      <Dialog open={enrollDialogOpen} onOpenChange={(open) => {
        setEnrollDialogOpen(open);
        if (!open) {
          setEnrollingCourseId(null);
          setSelectedCourseForEnrollment(null);
        }
      }}>
        <DialogContent className="fixed top-[20%] sm:top-20 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] sm:w-full max-w-lg rounded-xl shadow-lg border bg-white mx-4">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Confirm Enrollment</DialogTitle>
            <DialogDescription className="text-sm sm:text-base">
              {selectedCourseForEnrollment?.startDate && new Date(selectedCourseForEnrollment.startDate) > new Date() ? (
                <>
                  You are about to enroll in <strong>{selectedCourseForEnrollment?.title}</strong>.
                  <br /><br />
                  <span className="text-amber-600 block my-2">
                    ⚠️ This course starts on {new Date(selectedCourseForEnrollment.startDate).toLocaleDateString()}.
                  </span>
                  After enrollment, you will need to make payment to unlock the content on the start date.
                </>
              ) : (
                <>
                  You are about to enroll in <strong>{selectedCourseForEnrollment?.title}</strong>.
                  <br /><br />
                  After enrollment, you will need to make payment to unlock the content.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 mt-4">
            <Button variant="outline" onClick={() => setEnrollDialogOpen(false)} disabled={enrolling} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={handleEnroll} disabled={enrolling} className="w-full sm:w-auto">
              {enrolling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enrolling...
                </>
              ) : (
                'Enroll Now'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global Message Modal - FIXED: responsive sizing */}
      <AnimatePresence>
        {modal.show && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 max-w-[calc(100%-2rem)] sm:max-w-sm w-full text-center"
            >
              <div className="flex justify-center mb-4 sm:mb-6">
                {modal.type === "success" ? (
                  <CheckCircle className="h-14 w-14 sm:h-20 sm:w-20 text-green-500" />
                ) : (
                  <XCircle className="h-14 w-14 sm:h-20 sm:w-20 text-red-500" />
                )}
              </div>

              <h3 className="text-xl sm:text-2xl font-semibold mb-2 sm:mb-3">
                {modal.type === "success" ? "Success!" : "Error"}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 break-words">{modal.message}</p>

              <div className="h-1 bg-gray-100 rounded-full mt-6 sm:mt-8 overflow-hidden">
                <motion.div
                  className={`h-full ${modal.type === "success" ? "bg-green-500" : "bg-red-500"}`}
                  initial={{ width: "100%" }}
                  animate={{ width: "0%" }}
                  transition={{ duration: 3, ease: "linear" }}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
